import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Drawer,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconMessageCircle, IconSend, IconSparkles, IconHistory } from "@tabler/icons-react";
import { ownerApi, type ChatResponse } from "../api/client";
import { useOwnerScope } from "../context/OwnerScopeContext";
import { useConversationDrawer } from "../context/ConversationDrawerContext";
import { dollars } from "./ownerUi";

interface Message {
  id: number;
  role: "user" | "assistant";
  text: string;
  payload?: ChatResponse;
}

const SUGGESTIONS_MANY: string[] = [
  "Top 5 shops by revenue this month",
  "Which shop has the worst signed-doc coverage?",
  "Revenue per tech for the last 30 days",
  "Show me unsigned jobs",
];

const SUGGESTIONS_FEW: string[] = [
  "Revenue per tech for the last 30 days",
  "How many jobs were signed this week?",
  "Top earning team members for 90 days",
  "Show me unsigned jobs",
];

const SUGGESTIONS_ONE: string[] = [
  "Compare this shop to the others",
  "Revenue per tech for the last 30 days",
  "How many jobs were signed this week?",
  "Show me unsigned jobs",
];

// Refine prompts shown under each assistant response — pre-filled tweaks
// derived from the query_id, so the user doesn't have to retype.
function refinePrompts(queryId: string | null): string[] {
  switch (queryId) {
    case "revenue_by_day":
    case "revenue_by_charger":
    case "team_summary":
      return ["Same for last 90 days", "Same but signed only", "Compare to prior period"];
    case "legal_doc_coverage":
      return ["Break down by shop", "Show unsigned only", "Same for last 90 days"];
    case "jobs_search":
      return ["Approved only", "Unsigned only", "Last 7 days only"];
    case "recent_signed_docs":
      return ["Just paper signatures", "Last 7 days", "Group by shop"];
    default:
      return [];
  }
}

// Map a row's keys to a friendly table for a given query_id.
function renderRows(payload: ChatResponse, openConv: (cid: number, sid: number) => void) {
  const rows = payload.rows || [];
  if (!rows.length) return <Text size="sm" c="dimmed">No results.</Text>;
  const sample = rows[0];

  if (payload.query_id === "revenue_by_day") {
    return (
      <Table verticalSpacing={4}>
        <Table.Thead><Table.Tr><Table.Th>Day</Table.Th><Table.Th ta="right">Revenue</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (
            <Table.Tr key={i}>
              <Table.Td><Text size="xs">{String(r.day || "")}</Text></Table.Td>
              <Table.Td ta="right"><Text size="xs">{dollars(Number(r.revenue_cents || 0))}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  if (payload.query_id === "revenue_by_charger" || payload.query_id === "team_summary") {
    return (
      <Table verticalSpacing={4}>
        <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th ta="right">Jobs</Table.Th><Table.Th ta="right">Revenue</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (
            <Table.Tr key={i}>
              <Table.Td><Text size="xs">{String(r.name || "")}</Text></Table.Td>
              <Table.Td ta="right"><Text size="xs">{Number(r.jobs || 0)}</Text></Table.Td>
              <Table.Td ta="right"><Text size="xs">{dollars(Number(r.revenue_cents || 0))}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  if (payload.query_id === "legal_doc_coverage") {
    const r = rows[0] as Record<string, number>;
    return (
      <Stack gap={4}>
        <Text size="xs">Total: <b>{r.total_jobs}</b></Text>
        <Text size="xs">Signed: <b>{r.signed_jobs}</b></Text>
        <Text size="xs">Unsigned: <b>{r.unsigned_jobs}</b></Text>
      </Stack>
    );
  }

  if (payload.query_id === "jobs_search" || payload.query_id === "recent_signed_docs") {
    return (
      <Table verticalSpacing={4}>
        <Table.Thead><Table.Tr><Table.Th>Customer</Table.Th><Table.Th>Vehicle</Table.Th><Table.Th ta="right">Total</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => {
            const cid = Number(r.conversation_id || 0);
            const sid = Number(r.shop_id || 0);
            const clickable = cid > 0 && sid > 0;
            return (
              <Table.Tr
                key={i}
                style={clickable ? { cursor: "pointer" } : undefined}
                onClick={clickable ? () => openConv(cid, sid) : undefined}
              >
                <Table.Td><Text size="xs" fw={clickable ? 500 : 400} c={clickable ? "#4F46E5" : undefined}>{String(r.customer_name || "—")}</Text></Table.Td>
                <Table.Td><Text size="xs">{String(r.vehicle || "—")}</Text></Table.Td>
                <Table.Td ta="right"><Text size="xs">{dollars(Number(r.total_cents || 0))}</Text></Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  }

  // Fallback: generic key/value table from the first row.
  const keys = Object.keys(sample).slice(0, 4);
  return (
    <Table verticalSpacing={4}>
      <Table.Thead><Table.Tr>{keys.map((k) => <Table.Th key={k}>{k}</Table.Th>)}</Table.Tr></Table.Thead>
      <Table.Tbody>
        {rows.map((r, i) => (
          <Table.Tr key={i}>
            {keys.map((k) => <Table.Td key={k}><Text size="xs">{String((r as any)[k] ?? "")}</Text></Table.Td>)}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function ChatPanel() {
  const { effectiveShopIds, allShops } = useOwnerScope();
  const conv = useConversationDrawer();
  const [messages, setMessages] = useState<Message[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);

  // Scope-aware starter prompts.
  const suggestions = useMemo(() => {
    if (effectiveShopIds.length === 1) return SUGGESTIONS_ONE;
    if (effectiveShopIds.length >= 5 || effectiveShopIds.length === allShops.length) return SUGGESTIONS_MANY;
    return SUGGESTIONS_FEW;
  }, [effectiveShopIds.join(","), allShops.length]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text?: string) => {
    const prompt = (text ?? input).trim();
    if (!prompt || sending) return;
    setMessages((m) => [...m, { id: nextId.current++, role: "user", text: prompt }]);
    setHistory((h) => [prompt, ...h.filter((p) => p !== prompt)].slice(0, 5));
    setInput("");
    setSending(true);
    try {
      const resp = await ownerApi.chat(prompt, effectiveShopIds);
      setMessages((m) => [
        ...m,
        { id: nextId.current++, role: "assistant", text: resp.explanation, payload: resp },
      ]);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Something went wrong.";
      setMessages((m) => [...m, { id: nextId.current++, role: "assistant", text: detail }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack gap="xs" h="100%">
      <Box style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
        {messages.length === 0 ? (
          <Stack gap={6}>
            <Text size="sm" c="dimmed">Try one of these:</Text>
            {suggestions.map((s) => (
              <UnstyledButton
                key={s}
                onClick={() => send(s)}
                px={10}
                py={6}
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--mantine-color-gray-3)",
                  background: "white",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(79,70,229,0.05)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              >
                <Text size="sm">{s}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        ) : (
          <Stack gap="xs">
            {messages.map((m) =>
              m.role === "user" ? (
                <Group key={m.id} justify="flex-end">
                  <Paper p="xs" radius="md" bg="#EEF2FF" style={{ maxWidth: "85%" }}>
                    <Text size="sm">{m.text}</Text>
                  </Paper>
                </Group>
              ) : (
                <Stack key={m.id} gap={4}>
                  <Group gap={6} wrap="nowrap">
                    <IconSparkles size={14} stroke={1.5} color="#4F46E5" />
                    <Text size="sm" c="dimmed">{m.text}</Text>
                  </Group>
                  {m.payload?.rows?.length ? (
                    <Paper p="xs" radius="md" withBorder>
                      {renderRows(m.payload, (cid, sid) => conv.open(cid, sid))}
                      {m.payload.truncated && (
                        <Text size="10px" c="dimmed" mt={4}>Truncated — refine your question for fewer rows.</Text>
                      )}
                    </Paper>
                  ) : null}
                  {m.payload?.query_id ? (
                    <Group gap={4} wrap="wrap" mt={2}>
                      {refinePrompts(m.payload.query_id).map((r) => (
                        <UnstyledButton
                          key={r}
                          onClick={() => send(r)}
                          px={8}
                          py={2}
                          style={{
                            borderRadius: 999,
                            border: "1px solid rgba(79,70,229,0.3)",
                            background: "rgba(79,70,229,0.04)",
                            fontSize: 11,
                            color: "#4F46E5",
                            fontWeight: 500,
                          }}
                        >
                          ↪ {r}
                        </UnstyledButton>
                      ))}
                    </Group>
                  ) : null}
                </Stack>
              )
            )}
            <div ref={endRef} />
          </Stack>
        )}
      </Box>

      {history.length > 0 && (
        <Group gap={6} wrap="wrap" align="center">
          <IconHistory size={12} stroke={1.5} color="var(--mantine-color-gray-6)" />
          {history.map((h) => (
            <UnstyledButton
              key={h}
              onClick={() => send(h)}
              px={8}
              py={2}
              style={{
                borderRadius: 999,
                border: "1px solid var(--mantine-color-gray-3)",
                background: "white",
                fontSize: 11,
                color: "var(--mantine-color-gray-7)",
                maxWidth: 220,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={h}
            >
              {h}
            </UnstyledButton>
          ))}
        </Group>
      )}

      <Group gap="xs" wrap="nowrap" align="flex-end">
        <Textarea
          placeholder="Ask about revenue, team, jobs…"
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          autosize
          minRows={1}
          maxRows={4}
          style={{ flex: 1 }}
          disabled={sending}
        />
        <ActionIcon
          variant="filled"
          color="indigo"
          size="lg"
          onClick={() => send()}
          loading={sending}
          aria-label="Send"
        >
          <IconSend size={16} stroke={1.5} />
        </ActionIcon>
      </Group>
    </Stack>
  );
}

export function OwnerChatDrawer() {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Tooltip label="Ask the dashboard" position="left">
        <ActionIcon
          variant="filled"
          color="indigo"
          size="xl"
          radius="xl"
          aria-label="Open chat"
          onClick={() => setOpened(true)}
          style={{ position: "fixed", right: 20, bottom: 20, zIndex: 100, boxShadow: "0 6px 18px rgba(79,70,229,0.4)" }}
        >
          <IconMessageCircle size={22} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size="md"
        title="Ask the dashboard"
        styles={{ body: { display: "flex", flexDirection: "column", height: "calc(100vh - 60px)" } }}
      >
        <ChatPanel />
      </Drawer>
    </>
  );
}
